import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Trash2, GripVertical } from "lucide-react";

const KINDS = [
    ["text", "Short text"],
    ["textarea", "Long text"],
    ["yesno", "Yes / No"],
    ["number", "Number"],
    ["choice", "Multiple choice"],
];

export default function JobQuestionnaire({ jobId }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        (async () => {
            try { const { data } = await api.get(`/jobs/${jobId}/questionnaire`); setItems(data.questionnaire || []); }
            catch (err) { toast.error(formatApiError(err)); }
            finally { setLoading(false); }
        })();
    }, [jobId]);

    const add = () => setItems((arr) => [...arr, { label: "", kind: "text", required: false, choices: [] }]);
    const update = (i, k, v) => setItems((arr) => arr.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
    const remove = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));
    const save = async () => {
        setBusy(true);
        try {
            const cleaned = items.filter((x) => (x.label || "").trim()).map((x) => ({
                ...x,
                choices: x.kind === "choice" ? (x.choices || []).filter(Boolean) : null,
            }));
            await api.put(`/jobs/${jobId}/questionnaire`, cleaned);
            toast.success("Questionnaire saved");
            setItems(cleaned);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    if (loading) return <div className="text-sm font-mono text-foreground/80">Loading…</div>;
    return (
        <section className="border border-border bg-white p-6 space-y-4" data-testid="job-questionnaire-editor">
            <div className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="font-display font-bold text-xl">Applicant questionnaire</h2>
                    <p className="text-sm text-foreground/80 mt-1">These questions are shown to candidates on the public apply page. Keep it short — 2-4 questions max.</p>
                </div>
                <Button type="button" onClick={add} variant="outline" className="rounded-none h-10" data-testid="qn-add"><Plus className="w-4 h-4 mr-1" /> Add question</Button>
            </div>
            <div className="space-y-3">
                {items.length === 0 ? (
                    <div className="border border-dashed border-border p-6 text-center text-sm text-foreground/80">No screening questions yet.</div>
                ) : items.map((q, i) => (
                    <div key={i} className="border border-border p-3 space-y-2" data-testid={`qn-row-${i}`}>
                        <div className="flex items-start gap-2">
                            <GripVertical className="w-4 h-4 text-foreground/40 mt-2.5" />
                            <Input value={q.label} onChange={(e) => update(i, "label", e.target.value)} placeholder="Question text…" className="rounded-none h-10 flex-1" data-testid={`qn-label-${i}`} />
                            <Select value={q.kind} onValueChange={(v) => update(i, "kind", v)}>
                                <SelectTrigger className="rounded-none h-10 w-[150px]" data-testid={`qn-kind-${i}`}><SelectValue /></SelectTrigger>
                                <SelectContent>{KINDS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                            </Select>
                            <label className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest px-2 py-2 border border-border">
                                <Checkbox checked={q.required} onCheckedChange={(v) => update(i, "required", !!v)} className="rounded-none" data-testid={`qn-req-${i}`} /> Required
                            </label>
                            <button type="button" onClick={() => remove(i)} className="text-destructive hover:text-destructive/80 p-2" data-testid={`qn-remove-${i}`}>
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                        {q.kind === "choice" && (
                            <Input
                                value={(q.choices || []).join(", ")}
                                onChange={(e) => update(i, "choices", e.target.value.split(",").map((s) => s.trim()))}
                                placeholder="Comma-separated choices: Yes, No, Maybe"
                                className="rounded-none h-9 text-xs"
                                data-testid={`qn-choices-${i}`}
                            />
                        )}
                    </div>
                ))}
            </div>
            <div className="flex justify-end">
                <Button type="button" onClick={save} disabled={busy} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white h-10" data-testid="qn-save">
                    {busy ? "Saving…" : "Save questionnaire"}
                </Button>
            </div>
        </section>
    );
}
